import { z } from "zod";
import type { UsageGroup } from "./usage.ts";

export const modelPricingSchema = z.strictObject({
  inputPerMillion: z.number().nonnegative().optional(),
  outputPerMillion: z.number().nonnegative().optional(),
  cacheReadPerMillion: z.number().nonnegative().optional(),
  cacheWritePerMillion: z.number().nonnegative().optional(),
  inputPerMTokUsd: z.number().nonnegative().optional(),
  outputPerMTokUsd: z.number().nonnegative().optional(),
});
export type ModelPricing = z.infer<typeof modelPricingSchema>;

/**
 * Shared model catalog pricing metadata (USD per 1,000,000 tokens).
 * Source of truth across frontend and backend.
 */
export const MODEL_CATALOG_PRICING: Record<string, ModelPricing> = {
  // Anthropic
  "claude-3-7-sonnet": {
    inputPerMillion: 3,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.3,
    cacheWritePerMillion: 3.75,
  },
  "claude-3-5-sonnet": {
    inputPerMillion: 3,
    outputPerMillion: 15,
    cacheReadPerMillion: 0.3,
    cacheWritePerMillion: 3.75,
  },
  "claude-3-5-haiku": {
    inputPerMillion: 0.8,
    outputPerMillion: 4,
    cacheReadPerMillion: 0.08,
    cacheWritePerMillion: 1,
  },
  "claude-3-opus": {
    inputPerMillion: 15,
    outputPerMillion: 75,
    cacheReadPerMillion: 1.5,
    cacheWritePerMillion: 18.75,
  },

  // OpenAI
  "gpt-4o": {
    inputPerMillion: 2.5,
    outputPerMillion: 10,
    cacheReadPerMillion: 1.25,
  },
  "gpt-4o-mini": {
    inputPerMillion: 0.15,
    outputPerMillion: 0.6,
    cacheReadPerMillion: 0.075,
  },
  o1: {
    inputPerMillion: 15,
    outputPerMillion: 60,
    cacheReadPerMillion: 7.5,
  },
  "o3-mini": {
    inputPerMillion: 1.1,
    outputPerMillion: 4.4,
    cacheReadPerMillion: 0.55,
  },

  // Google Gemini
  "gemini-2.0-flash": {
    inputPerMillion: 0.1,
    outputPerMillion: 0.4,
  },
  "gemini-2.0-flash-thinking-exp-01-21": {
    inputPerMillion: 0.1,
    outputPerMillion: 0.4,
  },
  "gemini-1.5-pro": {
    inputPerMillion: 1.25,
    outputPerMillion: 5,
    cacheReadPerMillion: 0.3125,
  },
  "gemini-1.5-flash": {
    inputPerMillion: 0.075,
    outputPerMillion: 0.3,
    cacheReadPerMillion: 0.01875,
  },
};

/**
 * Normalizes model identifiers to resolve against the catalog.
 * Supports date-versioned suffixes (e.g. claude-3-7-sonnet-20250219)
 * and provider-prefixed IDs (e.g. anthropic/claude-3.5-sonnet).
 */
export function normalizeModelId(modelId: string): string {
  let m = modelId.toLowerCase().trim();
  const slashIdx = m.lastIndexOf("/");
  if (slashIdx !== -1) {
    m = m.slice(slashIdx + 1);
  }
  return m;
}

/**
 * Looks up pricing metadata for a given model and optional provider.
 * Returns undefined if no known pricing exists.
 */
export function getModelPricing(modelId: string, _provider?: string): ModelPricing | undefined {
  const norm = normalizeModelId(modelId);

  // Exact normalized match
  if (MODEL_CATALOG_PRICING[norm]) {
    return MODEL_CATALOG_PRICING[norm];
  }

  // Handle dot notation e.g. claude-3.5-sonnet vs claude-3-5-sonnet
  const dashed = norm.replace(/\./g, "-");
  if (MODEL_CATALOG_PRICING[dashed]) {
    return MODEL_CATALOG_PRICING[dashed];
  }

  // Date-suffix match (e.g. claude-3-7-sonnet-20250219 -> claude-3-7-sonnet)
  const strippedDate = dashed.replace(/-\d{8}$/, "");
  if (MODEL_CATALOG_PRICING[strippedDate]) {
    return MODEL_CATALOG_PRICING[strippedDate];
  }

  // Prefix match against catalog keys
  for (const [key, pricing] of Object.entries(MODEL_CATALOG_PRICING)) {
    if (dashed.startsWith(key)) {
      return pricing;
    }
  }

  return undefined;
}

export interface ModelUsageDimensions {
  inputTokens: number;
  outputTokens: number;
  cachedTokens?: number;
  cacheWriteTokens?: number;
}

/**
 * Computes monetary cost for a single model usage record.
 * Only includes cache pricing where both usage and pricing metadata support it.
 */
export function calculateModelCost(usage: ModelUsageDimensions, pricing: ModelPricing): number {
  let cost = 0;
  const inPrice = pricing.inputPerMillion ?? pricing.inputPerMTokUsd;
  if (inPrice !== undefined && inPrice > 0) {
    cost += (usage.inputTokens / 1_000_000) * inPrice;
  }
  const outPrice = pricing.outputPerMillion ?? pricing.outputPerMTokUsd;
  if (outPrice !== undefined && outPrice > 0) {
    cost += (usage.outputTokens / 1_000_000) * outPrice;
  }
  if (usage.cachedTokens && usage.cachedTokens > 0 && pricing.cacheReadPerMillion !== undefined) {
    cost += (usage.cachedTokens / 1_000_000) * pricing.cacheReadPerMillion;
  }
  if (usage.cacheWriteTokens && usage.cacheWriteTokens > 0 && pricing.cacheWritePerMillion !== undefined) {
    cost += (usage.cacheWriteTokens / 1_000_000) * pricing.cacheWritePerMillion;
  }
  return cost;
}

export interface EstimatedCostAggregation {
  totalUsd: number;
  unknownModelCount: number;
  unknownModels: string[];
  hasKnownCost: boolean;
  isPartial: boolean;
  byModelCost: Map<string, number | null>;
  byProviderCost: Map<string, number | null>;
}

/**
 * Aggregates estimated monetary cost across a list of usage groups.
 * Does not silently treat unknown pricing as $0.
 */
export function calculateEstimatedCost(byModel: UsageGroup[]): EstimatedCostAggregation {
  let totalUsd = 0;
  let hasKnownCost = false;
  const unknownModelsSet = new Set<string>();
  const byModelCost = new Map<string, number | null>();
  const byProviderCost = new Map<string, number | null>();

  for (const item of byModel) {
    const pricing = getModelPricing(item.model, item.provider);
    const modelKey = `${item.provider}/${item.model}`;

    if (pricing) {
      const cost = calculateModelCost(item, pricing);
      totalUsd += cost;
      hasKnownCost = true;
      byModelCost.set(modelKey, cost);

      const prevProv = byProviderCost.get(item.provider);
      byProviderCost.set(item.provider, (prevProv ?? 0) + cost);
    } else {
      unknownModelsSet.add(item.model);
      byModelCost.set(modelKey, null);
      if (!byProviderCost.has(item.provider)) {
        byProviderCost.set(item.provider, null);
      }
    }
  }

  const unknownModels = Array.from(unknownModelsSet);
  return {
    totalUsd: Math.round(totalUsd * 10000) / 10000,
    unknownModelCount: unknownModels.length,
    unknownModels,
    hasKnownCost,
    isPartial: unknownModels.length > 0,
    byModelCost,
    byProviderCost,
  };
}
